import { ActivatedRouteSnapshot, CanActivateFn, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { inject } from '@angular/core';
import { UsersService } from '../shared/services/users/users.service';
import { JwtUser } from '../shared/models/user';
import { Observable } from 'rxjs';

/**
 * Guard que verifica si el usuario tiene los roles necesarios para acceder a una ruta.
 * 
 * Uso en las rutas:
 * {
 *   path: 'ruta',
 *   component: ComponenteEjemplo,
 *   canActivate: [hasRoleGuard],
 *   data: { roles: ['ROLE1', 'ROLE2'] } // o data: { roles: 'SINGLE_ROLE' }
 * }
 */
export const hasRoleGuard: CanActivateFn = (
  route: ActivatedRouteSnapshot,
  state: RouterStateSnapshot
):
  Observable<boolean | UrlTree>
  | Promise<boolean | UrlTree>
  | boolean
  | UrlTree => {
  
  const usersService = inject(UsersService);
  const router = inject(Router);

  // Primero verificar si el usuario está logueado
  if (!usersService.canActivate()) {
    return router.createUrlTree(['/']);
  }

  // Obtener los roles requeridos desde la configuración de la ruta
  const requiredRoles = route.data['roles'];
  
  // Si no se especifican roles, permitir acceso (solo verificar login)
  if (!requiredRoles) {
    return true;
  }

  // Obtener el usuario actual
  const user: JwtUser | null = usersService.getUser();
  
  if (!user) {
    return router.createUrlTree(['/']);
  }

  // Obtener los grupos/roles del usuario
  const userGroups: string[] = user['cognito:groups'] || [];

  // Convertir a array si es un string
  const rolesArray: string[] = Array.isArray(requiredRoles) ? requiredRoles : [requiredRoles];

  // Verificar si el usuario tiene al menos uno de los roles requeridos
  const hasRole = rolesArray.some(role => userGroups.includes(role));

  if (hasRole) {
    return true;
  } else {
    // Redirigir a una página de acceso denegado o al home
    return router.createUrlTree(['/access-denied']); // o ['/'] si prefieres redirigir al home
  }
};